import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  IS_APPLE,
  KEY_MODIFIER_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';

/** Lexical dispatches KEY_MODIFIER_COMMAND on every modifier keypress but maps
 *  none of them to a command itself — that mapping is left to the app. Nothing
 *  did, so only the toolbar buttons worked; Cmd/Ctrl+B/I/U/Z/Shift+Z were
 *  silently ignored. Mirrors the key-matching Lexical's own Playground uses. */
const controlOrMeta = (event: KeyboardEvent): boolean => (IS_APPLE ? event.metaKey : event.ctrlKey);

const isPlainShortcut = (event: KeyboardEvent, code: string): boolean =>
  event.code === code && !event.altKey && !event.shiftKey && controlOrMeta(event);

const isBold = (event: KeyboardEvent): boolean => isPlainShortcut(event, 'KeyB');
const isItalic = (event: KeyboardEvent): boolean => isPlainShortcut(event, 'KeyI');
const isUnderline = (event: KeyboardEvent): boolean => isPlainShortcut(event, 'KeyU');
const isUndo = (event: KeyboardEvent): boolean => isPlainShortcut(event, 'KeyZ');

/** Cmd+Shift+Z on Apple, Ctrl+Y (the Windows/Linux convention) elsewhere —
 *  checked before isUndo so Shift+Z is never also treated as plain undo. */
const isRedo = (event: KeyboardEvent): boolean =>
  (event.code === 'KeyZ' && event.shiftKey && !event.altKey && controlOrMeta(event)) ||
  (!IS_APPLE && isPlainShortcut(event, 'KeyY'));

/** Wires the standard Bold / Italic / Underline / Undo / Redo keyboard
 *  shortcuts onto the same commands the toolbar buttons and HistoryPlugin
 *  already dispatch. */
export function ScriptShortcutsPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(
    () =>
      editor.registerCommand(
        KEY_MODIFIER_COMMAND,
        (event) => {
          if (isBold(event)) {
            event.preventDefault();
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            return true;
          }
          if (isItalic(event)) {
            event.preventDefault();
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            return true;
          }
          if (isUnderline(event)) {
            event.preventDefault();
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            return true;
          }
          if (isRedo(event)) {
            event.preventDefault();
            editor.dispatchCommand(REDO_COMMAND, undefined);
            return true;
          }
          if (isUndo(event)) {
            event.preventDefault();
            editor.dispatchCommand(UNDO_COMMAND, undefined);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
    [editor],
  );
  return null;
}
